import type {
  EventBindingCleanup,
  FallbackOptions,
  JobGuidManager,
  PusherEventCallback,
} from '@shipstation/async-client-manager/dist/types';
import type { MutableRefObject } from 'react';

export const LTL_PUSHER_TIMEOUT_MS = 30000;

// Booking now waits on a backend document-availability poll (~12.5s) before the "freight-book"
// Pusher event fires, on top of normal carrier booking time — give it more headroom than quotes.
export const LTL_BOOK_PUSHER_TIMEOUT_MS = 60000;

export type LtlFallbackOptions = {
  timeoutMs?: number;
  onTimeout?: () => void;
};

export type LtlPusherService = {
  subscribe: <T>(eventName: string, callback: (payload: T) => void, fallbackOptions?: LtlFallbackOptions) => () => void;
};

type LtlBindToEventFn = (
  eventName: string,
  callback: PusherEventCallback,
  channelName: string | undefined,
  options: FallbackOptions,
) => EventBindingCleanup;

type JobGuidManagerFactory = () => JobGuidManager;

// Builds the pusherService object consumed by useFreightQuoteProgress /
// useFreightBookProgress. Extracted here so both the FreightBrowserContainer
// and FreightBookingWizardContainer can share identical fallback logic.
//
// Pass separate pendingGuidsRef instances when wiring independent quote and
// book channels (as the wizard does), or a single ref when only one channel
// is needed (as the browser does).
export const makeLtlPusherService = (
  pendingGuidsRef: MutableRefObject<Set<string>>,
  bindToEvent: LtlBindToEventFn,
  jobGuidManagerFactory: JobGuidManagerFactory,
): LtlPusherService => ({
  subscribe: <T>(eventName: string, callback: (payload: T) => void, fallbackOptions?: LtlFallbackOptions) => {
    const manager = jobGuidManagerFactory();
    const unbind = bindToEvent(eventName, callback as PusherEventCallback, undefined, {
      timeoutMs: fallbackOptions?.timeoutMs ?? LTL_PUSHER_TIMEOUT_MS,
      jobGuidManager: manager,
      // eslint-disable-next-line no-unused-vars
      resetTimeoutCheck: (msg: object, _jobGuids: string[]) =>
        pendingGuidsRef.current.has((msg as Record<string, unknown>)?.messageGuid as string),
      // eslint-disable-next-line no-unused-vars
      completeCheck: (msg: object, _jobGuids: string[]) => {
        const m = msg as Record<string, unknown>;
        const data = m?.data as Record<string, unknown> | undefined;
        return data?.IsComplete === true && pendingGuidsRef.current.has(m?.messageGuid as string);
      },
      timeoutCompleteCheck: ({ messages }: { messages: object[]; retryCount: number }) =>
        messages.some((msg) => pendingGuidsRef.current.has(((msg as Record<string, unknown>)?.messageGuid as string) ?? '')),
      timeoutRetryCheck: ({ messages, retryCount }: { messages: object[]; retryCount: number }) =>
        !messages.some((msg) => pendingGuidsRef.current.has(((msg as Record<string, unknown>)?.messageGuid as string) ?? '')) &&
        retryCount < 3,
      timeoutFailureCheck: ({ messages, retryCount }: { messages: object[]; retryCount: number }) =>
        !messages.some((msg) => pendingGuidsRef.current.has(((msg as Record<string, unknown>)?.messageGuid as string) ?? '')) &&
        retryCount >= 3,
      // eslint-disable-next-line no-unused-vars
      onTimeout: (_args: { messages: object[]; jobGuids: string[] }) => fallbackOptions?.onTimeout?.(),
      logContext: {},
    });
    return unbind;
  },
});
